import { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Loader2, Mic } from "lucide-react";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import type { SoapNote } from "../../shared/types/encounter";
import { formatDateTime } from "../../shared/format";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { EncounterShell } from "./components/EncounterShell";
import { DictationModal } from "./components/DictationModal";
import type { DictationResponse } from "../../shared/api/dictation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";

/** The editable note (server owns updatedAt). */
type Draft = Omit<SoapNote, "updatedAt">;

/**
 * Structured outpatient examination note — the real Vietnamese form
 * (hỏi bệnh → khám theo cơ quan → đánh giá hai giai đoạn), not a generic
 * 4-box SOAP. Debounced autosave; the backend owns content_schema_version v2
 * and re-applies the VN default sentences for blank CLS / định-bệnh fields.
 */
export function EncounterNotesPage() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/encounters" replace />;

  return (
    <EncounterShell encounterId={id}>
      {(e) => (
        <NoteEditor
          encounterId={id}
          initial={e.soap}
          readOnly={e.status !== "IN_PROGRESS"}
        />
      )}
    </EncounterShell>
  );
}

function draftOf(s: SoapNote): Draft {
  return { history: s.history, exam: s.exam, assessment: s.assessment };
}

type Section = keyof Draft;

interface FieldDef {
  section: Section;
  key: string;
  label: string;
  hint?: string;
  rows?: number;
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "1 · Hỏi bệnh",
    fields: [
      {
        section: "history",
        key: "chiefComplaint",
        label: "Lý do đi khám",
        hint: "Đồng bộ với lý do khám hiển thị ở hàng đợi",
        rows: 2,
      },
      {
        section: "history",
        key: "presentIllness",
        label: "Bệnh sử (quá trình bệnh lý / TCCN)",
        rows: 4,
      },
      {
        section: "history",
        key: "personalHistory",
        label: "Tiền sử bản thân",
        rows: 2,
      },
      {
        section: "history",
        key: "familyHistory",
        label: "Tiền sử gia đình",
        rows: 2,
      },
    ],
  },
  {
    title: "2 · Khám theo cơ quan",
    fields: [
      { section: "exam", key: "status", label: "Tình trạng lúc khám", rows: 2 },
      { section: "exam", key: "general", label: "Toàn thân", rows: 2 },
      { section: "exam", key: "skin", label: "Da niêm", rows: 2 },
      { section: "exam", key: "headNeck", label: "Đầu mặt cổ", rows: 2 },
      { section: "exam", key: "cardio", label: "Tuần hoàn", rows: 2 },
      { section: "exam", key: "resp", label: "Hô hấp", rows: 2 },
      { section: "exam", key: "abdomen", label: "Tiêu hoá / bụng", rows: 2 },
      {
        section: "exam",
        key: "musculoskeletal",
        label: "Cơ xương khớp",
        rows: 2,
      },
      { section: "exam", key: "neuro", label: "Thần kinh", rows: 2 },
    ],
  },
  {
    title: "3 · Đánh giá",
    fields: [
      {
        section: "assessment",
        key: "priorClsResults",
        label: "Kết quả CLS nổi bật đã có trước đó",
        rows: 2,
      },
      {
        section: "assessment",
        key: "preliminaryProblem",
        label: "Chẩn đoán sơ bộ / vấn đề",
        rows: 2,
      },
      {
        section: "assessment",
        key: "preliminaryPlan",
        label: "Hướng xử trí sơ bộ",
        rows: 2,
      },
      {
        section: "assessment",
        key: "supplementaryLabUs",
        label: "Kết quả XN, siêu âm bổ sung",
        rows: 2,
      },
      {
        section: "assessment",
        key: "definitiveDiagnosis",
        label: "Chẩn đoán xác định",
        rows: 2,
      },
      {
        section: "assessment",
        key: "definitivePlan",
        label: "Hướng xử trí xác định",
        hint: "VD: Esomeprazol 40mg x 14 ngày + Smecta. Hẹn tái khám 7 ngày.",
        rows: 3,
      },
    ],
  },
];

function NoteEditor({
  encounterId,
  initial,
  readOnly,
}: {
  encounterId: string;
  initial: SoapNote;
  readOnly: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(draftOf(initial));
  const [state, setState] = useState<SaveState>("idle");
  const [dictationOpen, setDictationOpen] = useState(false);
  const lastSentRef = useRef<Draft>(draft);

  /**
   * Merge the AI-generated SOAP back into the existing note. We append (don't
   * overwrite) so dictation can't silently clobber notes the doctor already
   * typed. The doctor still reviews each field before autosave fires.
   */
  function applyDictation(res: DictationResponse) {
    setDraft((prev) => ({
      ...prev,
      history: {
        ...prev.history,
        presentIllness: mergeText(prev.history.presentIllness, res.soap.subjective),
      },
      exam: {
        ...prev.exam,
        general: mergeText(prev.exam.general, res.soap.objective),
      },
      assessment: {
        ...prev.assessment,
        preliminaryProblem: mergeText(
          prev.assessment.preliminaryProblem,
          res.soap.assessment
        ),
        preliminaryPlan: mergeText(prev.assessment.preliminaryPlan, res.soap.plan),
      },
    }));
    setState("dirty");
    setDictationOpen(false);
  }

  // Resync when the underlying server note changes (e.g., another tab saved).
  useEffect(() => {
    const next = draftOf(initial);
    setDraft(next);
    lastSentRef.current = next;
    setState("idle");
  }, [initial.updatedAt]);

  const debounced = useDebounce(draft, 600);

  const save = useMutation({
    mutationFn: (data: Draft) => encountersApi.updateSoap(encounterId, data),
    onMutate: () => setState("saving"),
    onSuccess: () => {
      setState("saved");
      qc.invalidateQueries({ queryKey: encounterQueryKeys.detail(encounterId) });
    },
    onError: () => setState("error"),
  });

  useEffect(() => {
    if (readOnly) return;
    if (JSON.stringify(lastSentRef.current) === JSON.stringify(debounced)) {
      return;
    }
    lastSentRef.current = debounced;
    save.mutate(debounced);
  }, [debounced, readOnly]);

  function setField(section: Section, key: string, v: string) {
    setDraft((prev) => ({
      ...prev,
      [section]: { ...prev[section], [key]: v },
    }));
    setState("dirty");
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 border-b py-4">
        <CardTitle>Phiếu khám bệnh</CardTitle>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDictationOpen(true)}
              title="Thu âm bệnh án, AI tạo bản nháp S–O–A–P"
            >
              <Mic />
              Thu âm bệnh án
            </Button>
          )}
          {!readOnly && (
            <SaveIndicator state={state} updatedAt={initial.updatedAt} />
          )}
        </div>
      </CardHeader>

      <DictationModal
        encounterId={encounterId}
        open={dictationOpen}
        onClose={() => setDictationOpen(false)}
        onApply={applyDictation}
        chiefComplaint={initial.history?.chiefComplaint ?? null}
      />

      <CardContent className="space-y-8 pt-6">
        {SECTIONS.map((sec) => (
          <section key={sec.title} className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {sec.title}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {sec.fields.map((f) => {
                const value =
                  (draft[f.section] as unknown as Record<string, string>)[
                    f.key
                  ] ?? "";
                const fieldId = `soap-${f.section}-${f.key}`;
                return (
                  <div
                    key={`${f.section}.${f.key}`}
                    className="space-y-1.5"
                  >
                    <Label htmlFor={fieldId} className="font-medium">
                      {f.label}
                      {f.hint && (
                        <span className="ml-1 font-normal text-muted-foreground">
                          · {f.hint}
                        </span>
                      )}
                    </Label>
                    <Textarea
                      id={fieldId}
                      rows={f.rows ?? 2}
                      value={value}
                      onChange={(e) => setField(f.section, f.key, e.target.value)}
                      readOnly={readOnly}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

/** Append AI-generated text to whatever the doctor already has, with a separator. */
function mergeText(existing: string | undefined, addition: string): string {
  const e = (existing ?? "").trim();
  const a = (addition ?? "").trim();
  if (!a) return e;
  if (!e) return a;
  return `${e}\n\n--- AI nháp ---\n${a}`;
}

function SaveIndicator({
  state,
  updatedAt,
}: {
  state: SaveState;
  updatedAt: string;
}) {
  if (state === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
        Đang lưu…
      </span>
    );
  }
  if (state === "saved" || state === "idle") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
        <Check className="size-3.5" />
        Đã lưu lúc {formatDateTime(updatedAt)}
      </span>
    );
  }
  if (state === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-destructive">
        <AlertTriangle className="size-3.5" />
        Lỗi lưu
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      Đang chỉnh sửa…
    </span>
  );
}
